import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { getServiceById } from '../services/api';
import { createStripeSubscription, createSetupIntent, getUserSubscriptions } from '../services/subscriptionService';
import { updateUserProfile } from '../services/userService';
import { getUserProfile } from '../services/authService';
import './Subscribe.css';

// Load Stripe outside of component render cycle
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Component that contains form for stripe
function CheckoutForm({ packageId, packageDetails, trialPeriodDays, onSuccess, onError }) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();

  // Get setup intent for saving the card
  useEffect(() => {
    const getSetupIntent = async () => {
      try {
        const setupIntent = await createSetupIntent();
        setClientSecret(setupIntent.client_secret);
      } catch (error) {
        console.error('Error getting setup intent:', error);
        setErrorMessage('Failed to initialize payment form. Please try again.');
      }
    };

    getSetupIntent();
  }, []);

  // Fetch and pre-fill user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoadingProfile(true);
        const userProfile = await getUserProfile();
        
        // Pre-fill form with user data from the database
        setBillingDetails(prevDetails => ({
          ...prevDetails,
          email: userProfile.email || currentUser.email || '',
          name: userProfile.first_name && userProfile.last_name 
            ? `${userProfile.first_name} ${userProfile.last_name}`.trim()
            : `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          phone: userProfile.phone || currentUser.phone || '',
          address: userProfile.address 
            ? (typeof userProfile.address === 'string' ? JSON.parse(userProfile.address) : userProfile.address)
            : prevDetails.address,
        }));
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fall back to currentUser data if profile fetch fails
        setBillingDetails(prevDetails => ({
          ...prevDetails,
          email: currentUser.email || '',
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          phone: currentUser.phone || '',
          address: currentUser.address 
            ? (typeof currentUser.address === 'string' ? JSON.parse(currentUser.address) : currentUser.address)
            : prevDetails.address,
        }));
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setBillingDetails(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    // Validate form
    if (!billingDetails.name) {
      setErrorMessage('Please enter your name');
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      // Create payment method and confirm setup intent
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: billingDetails.name,
            email: billingDetails.email,
            phone: billingDetails.phone,
            address: billingDetails.address
          },
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message);
        setProcessing(false);
        return;
      }

      // Create subscription with the payment method ID
      const subscription = await createStripeSubscription(
        packageId,
        result.setupIntent.payment_method,
        trialPeriodDays
      );

      // Update user profile with address and phone data
      if (currentUser) {
        try {
          // Store address as JSON string
          const userProfileUpdate = {
            phone: billingDetails.phone,
            address: JSON.stringify(billingDetails.address)
          };
          
          await updateUserProfile(userProfileUpdate);
        } catch (profileError) {
          console.error('Error updating user profile:', profileError);
          // Continue with subscription success even if profile update fails
        }
      }

      // Call the success handler with the subscription
      onSuccess(subscription);
    } catch (error) {
      console.error('Subscription error:', error);
      setErrorMessage(error.detail || 'Failed to process subscription. Please try again.');
      // Call the error handler with the error
      onError(error);
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  // Check if there's a trial period
  const hasTrial = trialPeriodDays > 0;

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      {loadingProfile && (
        <div className="loading-message">
          <p>Loading your profile information...</p>
        </div>
      )}
      
      <div className="form-section">
        <h3>Contact Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Name on Card</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Jane Doe"
              required
              value={billingDetails.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="jane.doe@example.com"
              required
              value={billingDetails.email}
              onChange={handleInputChange}
              disabled={currentUser && currentUser.email}
              className={currentUser && currentUser.email ? 'disabled-input' : ''}
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="(123) 456-7890"
            value={billingDetails.phone}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Billing Address</h3>
        <div className="form-group">
          <label htmlFor="address.line1">Address Line 1</label>
          <input
            id="address.line1"
            name="address.line1"
            placeholder="123 Main St."
            required
            value={billingDetails.address.line1}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="address.line2">Address Line 2 (Optional)</label>
          <input
            id="address.line2"
            name="address.line2"
            placeholder="Apt 4B, Floor 2, etc."
            value={billingDetails.address.line2 || ''}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.city">City</label>
            <input
              id="address.city"
              name="address.city"
              placeholder="San Francisco"
              required
              value={billingDetails.address.city}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.state">State</label>
            <input
              id="address.state"
              name="address.state"
              placeholder="CA"
              required
              value={billingDetails.address.state}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.postal_code">ZIP / Postal Code</label>
            <input
              id="address.postal_code"
              name="address.postal_code"
              placeholder="94103"
              required
              value={billingDetails.address.postal_code}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.country">Country</label>
            <select
              id="address.country"
              name="address.country"
              value={billingDetails.address.country}
              onChange={handleInputChange}
              required
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Payment Information</h3>
        <div className="card-element-container">
          <label htmlFor="card-element">Credit or debit card</label>
          <div className="card-element">
            <CardElement id="card-element" options={cardElementOptions} />
          </div>
        </div>

        {hasTrial && (
          <div className="trial-notice-inline">
            <p>Your card will not be charged until your {trialPeriodDays}-day trial period ends.</p>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="alert alert-danger">
          {errorMessage}
        </div>
      )}

      <div className="form-actions">
        <button
          className="btn btn-primary checkout-button"
          type="submit"
          disabled={!stripe || processing}
        >
          {processing
            ? "Processing..."
            : hasTrial
              ? `Start ${trialPeriodDays}-Day Free Trial`
              : `Subscribe for ${packageDetails.price}`}
        </button>
      </div>
    </form>
  );
}

function Subscribe() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);
  const [subscriptionComplete, setSubscriptionComplete] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user already has this subscription
  useEffect(() => {
    const checkExistingSubscription = async () => {
      // Only check if the user is signed in
      if (!currentUser) {
        setCheckingSubscription(false);
        return;
      }

      try {
        // Get package ID from URL params
        const params = new URLSearchParams(location.search);
        const packageId = params.get('package');
        
        if (!packageId) {
          setCheckingSubscription(false);
          return;
        }

        // Get user's active subscriptions
        const userSubscriptions = await getUserSubscriptions();
        
        // Check if user already has an active subscription for this package
        const existingSubscription = userSubscriptions.find(
          sub => sub.package_id === parseInt(packageId) && ['active', 'trialing'].includes(sub.status)
        );
        
        if (existingSubscription) {
          // Redirect to profile page with subscription section
          navigate('/profile#subscriptions');
        } else {
          setCheckingSubscription(false);
        }
      } catch (error) {
        console.error('Error checking existing subscriptions:', error);
        setCheckingSubscription(false);
      }
    };

    checkExistingSubscription();
  }, [currentUser, location.search, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if we're still checking subscription status
      if (checkingSubscription) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get package ID and trial flag from URL params
        const params = new URLSearchParams(location.search);
        const packageId = params.get('package');
        
        // Get trial value from URL - might be a boolean '1' or a number of days
        const trialParam = params.get('trial');
        
        // Parse trial days - could be just a flag '1' or actual number of days
        let parsedTrialDays = 0;
        if (trialParam) {
          if (trialParam === '1') {
            // Default to 30 days if just flagged with '1'
            parsedTrialDays = 30;
          } else {
            // Try to parse as integer
            const days = parseInt(trialParam, 10);
            if (!isNaN(days) && days > 0) {
              parsedTrialDays = days;
            }
          }
        }
        
        setTrialPeriodDays(parsedTrialDays);
        
        if (!packageId) {
          throw new Error('No package selected');
        }
        
        // Fetch package details
        const packageData = await getServiceById(packageId);
        setPackageDetails(packageData);
        
        // If we have trial info in the package attributes and no specific days from URL
        if (packageData.attributes?.trial && (trialParam === '1' || !trialParam)) {
          const packageTrialDays = parseInt(packageData.attributes.trial, 10);
          if (!isNaN(packageTrialDays) && packageTrialDays > 0) {
            setTrialPeriodDays(packageTrialDays * 30); // Convert months to days
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading subscription details:', error);
        setError('Failed to load subscription details. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search, checkingSubscription]);

  const handleSubscriptionSuccess = (subscriptionData) => {
    setSubscription(subscriptionData);
    setSubscriptionComplete(true);
    
    // Redirect to confirmation page
    setTimeout(() => {
      navigate(`/subscription/confirmation?id=${subscriptionData.id}`);
    }, 2000);
  };

  const handleSubscriptionError = (error) => {
    console.error('Subscription error:', error);
    setError(error.detail || 'Failed to process subscription. Please try again.');
  };

  if (checkingSubscription) {
    return <div className="loading-container">Checking subscription status...</div>;
  }

  if (loading) {
    return <div className="loading-container">Loading subscription details...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => navigate('/books')}>
          Back to Packages
        </button>
      </div>
    );
  }

  if (subscriptionComplete) {
    return (
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h2>Subscription Successful!</h2>
        <p>Redirecting to your subscription details...</p>
      </div>
    );
  }

  // Determine if we have a trial period
  const hasTrial = trialPeriodDays > 0;

  return (
    <div className="subscribe-page">
      <div className="subscribe-container">
        <div className="subscribe-content">
          <h1>Subscribe to {packageDetails.title}</h1>
          
          {hasTrial && (
            <div className="trial-notice">
              <h3>Free Trial Period</h3>
              <p>You are signing up for a {trialPeriodDays}-day free trial period. 
              Your card will be validated but not charged until the trial period ends.</p>
            </div>
          )}
          
          <div className="subscription-container">
            <div className="subscription-summary">
              <h2>Package Summary</h2>
              <div className="package-details">
                <p><strong>Package:</strong> {packageDetails.title}</p>
                <p><strong>Price:</strong> {packageDetails.price}</p>
                {hasTrial && (
                  <p><strong>Trial Period:</strong> {trialPeriodDays} days</p>
                )}
                <p><strong>Billing:</strong> Monthly</p>
                
                <div className="package-description">
                  <p>{packageDetails.description}</p>
                </div>
              </div>
            </div>
            
            <div className="payment-form-container">
              <h2>{hasTrial ? 'Payment Information for After Trial' : 'Payment Information'}</h2>
              
              <Elements stripe={stripePromise}>
                <CheckoutForm 
                  packageId={packageDetails.id}
                  packageDetails={packageDetails}
                  trialPeriodDays={trialPeriodDays}
                  onSuccess={handleSubscriptionSuccess}
                  onError={handleSubscriptionError}
                />
              </Elements>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Subscribe; 